import { FormEvent, useEffect, useState } from 'react';
import {
    Button,
    Card,
    Col,
    DatePicker,
    Form,
    Input,
    List,
    Modal,
    notification,
    Radio,
    Row,
    Space,
} from 'antd';
import styled from 'styled-components';

import { PaddedSection } from 'common/components';
import { colors, fontSizes } from 'theme/variables';

import { PaymentType, Subscription, SubscriptionPurchaseData } from '../models/userModel';
import { useForm } from 'antd/lib/form/Form';
import { useDispatch, useSelector } from 'react-redux';
import { getSubscriptionsTiersRequest, PurchaseSubscriptionRequest } from '../actions/userActions';
import { UserState } from '../reducers/userReducer';
import { dateFormat } from 'config/config';
import { isAdmin } from '../selectors/userSelectors';
import { openModal } from 'common/helpers/modal';
import moment from 'moment';
import {
    isWithinOneMonthBeforeExpiry,
    getSubscriptionStatusIndicator,
    wordCapitaliser,
} from 'common/helpers/utils';

const SubscriptionCard = styled(Card)<{
    hasActiveSubscription: number;
    subscriptionExpiresAt: string;
}>(
    ({ hasActiveSubscription, subscriptionExpiresAt }) => `
    border-color: ${
        hasActiveSubscription
            ? getSubscriptionStatusIndicator(subscriptionExpiresAt).color
            : colors.antdBorder
    };    border-width: 2px;
    height:100%;
`,
);

const SubscriptionCardButtonWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    flex-wrap: wrap;
`;

const TierButton = styled(Button)`
    display: block;
    float: right;
    margin: 15px 0 5px 0;
`;

type Props = {
    profile: UserState;
    userSubscription?: Subscription;
};

export const SubscriptionCards = ({ profile, userSubscription }: Props) => {
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [selectedPackageTier, setSelectedPackageTier] = useState<number | undefined>();
    const [modalForm] = useForm<SubscriptionPurchaseData>();
    const dispatch = useDispatch();
    const isUserAdmin = useSelector(isAdmin);
    const { subscriptionTiers } = profile;
    const { status, expiresAt, tierIndex, id, type, paymentPeriods } = userSubscription || {};
    const isOneMonthBeforeExpiry = isWithinOneMonthBeforeExpiry(expiresAt);
    const hasActiveSubscription = status === 'ACTIVE';
    useEffect(() => {
        if (isUserAdmin) {
            dispatch(getSubscriptionsTiersRequest());
        }
    }, [dispatch, isUserAdmin]);

    const handleButtonClick = (tier: number) => () => {
        if (!userSubscription || status !== 'ACTIVE') {
            setSelectedPackageTier(tier);
            setIsPaymentModalOpen(true);
        } else {
            showNotification(tier);
        }
    };

    const handleSubscribe = async () => {
        try {
            await modalForm.validateFields();
            dispatch(
                PurchaseSubscriptionRequest(
                    parseSubscriptionPurchaseData(modalForm.getFieldsValue()),
                ),
            );
            handleModalClose();
        } catch (error) {
            console.error('Validation Failed:', error);
        }
    };

    const parseSubscriptionPurchaseData = (data?: SubscriptionPurchaseData) => ({
        ...data,
        starting_on: data?.starting_on.format(dateFormat),
        item: selectedPackageTier ? selectedPackageTier : 1,
        type: data?.type.toUpperCase() as string,
    });

    const handleModalClose = () => {
        setSelectedPackageTier(undefined);
        setIsPaymentModalOpen(false);
    };

    const showNotification = (tier: number) => {
        const description =
            tier > tierIndex
                ? 'In order to upgrade your Cenefits subscription, cancel the current subscription first.'
                : 'In order to downgrade your Cenefits subscription, cancel the current subscription first.';

        const args = {
            message: 'Notification',
            description,
            duration: 5,
        };
        notification.info(args);
    };
    const openCancelSubscriptionModal = (e: FormEvent) => {
        e.preventDefault();
        return openModal({
            okText: 'Cancel Subscription',
            cancelText: 'Cancel',
            callback: () => {
                dispatch({
                    type: 'CANCEL_SUBSCRIPTION_REQUEST',
                    payload: id,
                });
            },
            title: 'Warning: This action will cancel subscription, do you want to proceed?',
        });
    };
    const renewSubscription = () => {
        dispatch(
            PurchaseSubscriptionRequest({
                item: tierIndex,
                payment_periods: paymentPeriods,
                starting_on: moment(expiresAt).format('YYYY-MM-DD'),
                type: wordCapitaliser(type),
            }),
        );
    };

    const renderTierButton = (tier: number) => {
        if (!hasActiveSubscription) {
            return <TierButton onClick={handleButtonClick(tier)}>Subscribe</TierButton>;
        }

        if (tier < tierIndex + 1) {
            return <TierButton onClick={handleButtonClick(tier)}>Downgrade</TierButton>;
        } else if (tier > tierIndex + 1) {
            return <TierButton onClick={handleButtonClick(tier)}>Upgrade</TierButton>;
        }
        return (
            <>
                <SubscriptionCardButtonWrapper>
                    <TierButton
                        danger
                        onClick={(e: FormEvent) => {
                            openCancelSubscriptionModal(e);
                        }}
                    >
                        Cancel
                    </TierButton>
                    {isOneMonthBeforeExpiry && (
                        <TierButton onClick={renewSubscription}>Renew</TierButton>
                    )}
                </SubscriptionCardButtonWrapper>
            </>
        );
    };

    return (
        <>
            <PaddedSection header="Buy/Upgrade Subscription">
                <Row gutter={16}>
                    {subscriptionTiers
                        .sort((a, b) => a.price - b.price)
                        .map(
                            ({
                                name,
                                title,
                                maxAvailableNumberOfUsers,
                                price,
                                description,
                                pk,
                            }) => (
                                <Col key={title} span={6}>
                                    <SubscriptionCard
                                        size="small"
                                        title={name}
                                        hasActiveSubscription={
                                            hasActiveSubscription && tierIndex + 1 === pk
                                        }
                                        subscriptionExpiresAt={expiresAt}
                                    >
                                        <List>
                                            <List.Item>
                                                Max users: {maxAvailableNumberOfUsers}
                                                <List.Item>Description: {description}</List.Item>
                                                <List.Item>Price: {price}</List.Item>
                                            </List.Item>
                                        </List>
                                        {renderTierButton(pk)}
                                    </SubscriptionCard>
                                </Col>
                            ),
                        )}
                </Row>
            </PaddedSection>
            <Modal
                visible={isPaymentModalOpen}
                onCancel={handleModalClose}
                onOk={handleSubscribe}
                okText={userSubscription ? 'Upgrade' : 'Subscribe'}
            >
                Select type of payment:
                <Form form={modalForm}>
                    <Form.Item name="type" initialValue={PaymentType.Monthly}>
                        <Radio.Group>
                            <Space direction="vertical">
                                <Radio value={PaymentType.Monthly}>{PaymentType.Monthly}</Radio>
                                <Radio value={PaymentType.Annual}>{PaymentType.Annual}</Radio>
                            </Space>
                        </Radio.Group>
                    </Form.Item>
                    Enter number of payment periods
                    <Form.Item
                        name="payment_periods"
                        rules={[
                            { required: true, message: 'Number of payment periods is required' },
                        ]}
                    >
                        <Input type="number"></Input>
                    </Form.Item>
                    Select Subscription Start Date
                    <Form.Item
                        name="starting_on"
                        rules={[{ required: true, message: 'Subscription start date is required' }]}
                    >
                        <DatePicker></DatePicker>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};
